import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import moment from 'moment';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { DataLayerTableColumns } from 'src/app/models/tableColumns/dataLayerTableColumn.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { DataLayerItemTypeSelectOptions } from 'src/app/models/selectOptions/dataLayerItemTypeSelectOptions.model';
import {
  isFileExtensionValue,
  extractFileExtension,
  FILE_EXTENSION_VALUE_PREFIX,
} from 'src/app/models/selectOptions/dataLayerItemTypeSelectOptions.model';
import { FindingAiServiceSelectOptions } from 'src/app/models/selectOptions/findingAiServiceSelectOptions.mode';
import { checkAllGivenIdsExistInOptions } from './utils';
import { DataLayerHttpService } from 'src/app/services/httpServices/dataLayerHttpService/data-layer-http.service';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';

interface FetchFilterOptionsConfig {
  ids: string[];
  filterName: string;
  filterType: FilterTypesEnum;
  filters: DynamicMultiSelectFilters;
  transformDisplayName?: (text: string) => string;
}

export class DataLayerFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public search: TextFilter;
  public dataTypeFilters: MultiSelectFilters;
  public sensitivityLabelFilters: MultiSelectFilters;
  public serviceFilters: MultiSelectFilters;
  public actorFilters: DynamicMultiSelectFilters;
  public agentFilters: DynamicMultiSelectFilters;

  public readonly defaultSortColumn: ITableColumn | undefined = DataLayerTableColumns.find(
    (column) => column.sortKey === 'accessCount'
  );
  public readonly sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: true } : undefined
  );

  constructor(
    private readonly _router: Router,
    private readonly _dataLayerHttpService: DataLayerHttpService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe
  ) {
    this._initializeFilters();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    // Separate dataType values into type (email/website) and fileExtension
    const allDataTypeValues = this.dataTypeFilters.getSelectedFiltersValues();
    const typeValues: string[] = [];
    const fileExtensionValues: string[] = [];

    allDataTypeValues.forEach((value) => {
      if (isFileExtensionValue(value)) {
        fileExtensionValues.push(extractFileExtension(value));
      } else {
        typeValues.push(value);
      }
    });

    return {
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      search: this.search.getFilterValue(),
      dataType: typeValues.length > 0 ? typeValues : undefined,
      fileExtension: fileExtensionValues.length > 0 ? fileExtensionValues : undefined,
      sensitivityLabel: this.sensitivityLabelFilters.getSelectedFiltersValues(),
      service: this.serviceFilters.getSelectedFiltersValues(),
      actor: this.actorFilters.getSelectedFiltersValues(),
      agent: this.agentFilters.getSelectedFiltersValues(),
    };
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    return [
      ...this.dataTypeFilters.getSelectedOptions(),
      ...this.sensitivityLabelFilters.getSelectedOptions(),
      ...this.serviceFilters.getSelectedOptions(),
      ...this.actorFilters.getSelectedOptions(),
      ...this.agentFilters.getSelectedOptions(),
    ];
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.Search:
          this.search.update(value as string | undefined);
          needUpdate = true;
          break;
        default:
          break;
      }
    } else {
      if (value instanceof Array) {
        if (value.length === 0) {
          needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          value.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          this._updateAllOptions();

          needUpdate =
            this.dataTypeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.DataType] || []) || needUpdate;
          needUpdate =
            this.sensitivityLabelFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.SensitivityLabel] || []) ||
            needUpdate;
          needUpdate =
            this.serviceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AiService] || []) || needUpdate;
          needUpdate = this.actorFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Actor] || []) || needUpdate;
          needUpdate =
            this.agentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AIAgentName] || []) || needUpdate;
        }
      }
    }

    if (needUpdate) {
      this._updateAllOptions();
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.DataType:
        return this.dataTypeFilters.getOptions$();
      case FilterTypesEnum.SensitivityLabel:
        return this.sensitivityLabelFilters.getOptions$();
      case FilterTypesEnum.AiService:
        return this.serviceFilters.getOptions$();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getOptions$();
      case FilterTypesEnum.AIAgentName:
        return this.agentFilters.getOptions$();
      default:
        throw new Error(`DataLayerFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    let needUpdate = false;

    const dataTypeOptionsToRemove = options.filter((opt) => opt.filterType === FilterTypesEnum.DataType);
    if (dataTypeOptionsToRemove.length > 0) {
      const currentDataTypeOptions = this.dataTypeFilters.getSelectedOptions();
      const remainingDataTypeOptions = currentDataTypeOptions.filter(
        (opt) => !dataTypeOptionsToRemove.some((toRemove) => toRemove.id === opt.id)
      );
      if (this.dataTypeFilters.updateIfNeeded(remainingDataTypeOptions)) {
        needUpdate = true;
      }
    }

    const sensitivityLabelOptionsToRemove = options.filter(
      (opt) => opt.filterType === FilterTypesEnum.SensitivityLabel
    );
    if (sensitivityLabelOptionsToRemove.length > 0) {
      const currentSensitivityLabelOptions = this.sensitivityLabelFilters.getSelectedOptions();
      const remainingSensitivityLabelOptions = currentSensitivityLabelOptions.filter(
        (opt) => !sensitivityLabelOptionsToRemove.some((toRemove) => toRemove.id === opt.id)
      );
      if (this.sensitivityLabelFilters.updateIfNeeded(remainingSensitivityLabelOptions)) {
        needUpdate = true;
      }
    }

    const serviceOptionsToRemove = options.filter((opt) => opt.filterType === FilterTypesEnum.AiService);
    if (serviceOptionsToRemove.length > 0) {
      const currentServiceOptions = this.serviceFilters.getSelectedOptions();
      const remainingServiceOptions = currentServiceOptions.filter(
        (opt) => !serviceOptionsToRemove.some((toRemove) => toRemove.id === opt.id)
      );
      if (this.serviceFilters.updateIfNeeded(remainingServiceOptions)) {
        needUpdate = true;
      }
    }

    const actorOptionsToRemove = options.filter((opt) => opt.filterType === FilterTypesEnum.Actor);
    if (actorOptionsToRemove.length > 0) {
      const currentActorOptions = this.actorFilters.getSelectedOptions();
      const remainingActorOptions = currentActorOptions.filter(
        (opt) => !actorOptionsToRemove.some((toRemove) => toRemove.id === opt.id)
      );
      if (this.actorFilters.updateIfNeeded(remainingActorOptions)) {
        needUpdate = true;
      }
    }

    const agentOptionsToRemove = options.filter((opt) => opt.filterType === FilterTypesEnum.AIAgentName);
    if (agentOptionsToRemove.length > 0) {
      const currentAgentOptions = this.agentFilters.getSelectedOptions();
      const remainingAgentOptions = currentAgentOptions.filter(
        (opt) => !agentOptionsToRemove.some((toRemove) => toRemove.id === opt.id)
      );
      if (this.agentFilters.updateIfNeeded(remainingAgentOptions)) {
        needUpdate = true;
      }
    }

    if (needUpdate) {
      this._updateAllOptions();
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    throw new Error(`DataLayerFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    if ('fromDate' in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
      const fromDateMoment =
        keyValueFilters['fromDate'] && moment(keyValueFilters['fromDate'][0], 'x').isValid()
          ? moment(keyValueFilters['fromDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const fromDate = fromDateMoment && !fromDateMoment.isAfter(moment()) ? fromDateMoment.toDate() : undefined;

      this.fromDate.update(fromDate);
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
      const toDateMoment =
        keyValueFilters['toDate'] && moment(keyValueFilters['toDate'][0], 'x').isValid()
          ? moment(keyValueFilters['toDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const toDate = toDateMoment && !toDateMoment.isAfter(moment()) ? toDateMoment.toDate() : undefined;

      this.toDate.update(toDate);
      needUpdate = true;
    }

    if ('search' in keyValueFilters || this.search.getFilterValue() !== undefined) {
      this.search.update(keyValueFilters['search'] ? keyValueFilters['search'][0] : undefined);
      needUpdate = true;
    }

    // Combine dataType and fileExtension values for the unified dataTypeFilters
    const hasDataTypeKey = 'dataType' in keyValueFilters;
    const hasFileExtensionKey = 'fileExtension' in keyValueFilters;
    const hasCurrentDataTypeValues = this.dataTypeFilters.getSelectedFiltersValues().length > 0;

    if (hasDataTypeKey || hasFileExtensionKey || hasCurrentDataTypeValues) {
      // Combine type values (email/website) with prefixed file extension values
      const typeValues = keyValueFilters['dataType'] || [];
      const fileExtValues = (keyValueFilters['fileExtension'] || []).map(
        (ext) => `${FILE_EXTENSION_VALUE_PREFIX}${ext}`
      );
      const combinedValues = [...typeValues, ...fileExtValues];

      if (this.dataTypeFilters.updateByValuesIfNeeded(combinedValues)) {
        needUpdate = true;
      }
    }

    if ('sensitivityLabel' in keyValueFilters || this.sensitivityLabelFilters.getSelectedFiltersValues().length > 0) {
      if (this.sensitivityLabelFilters.updateByValuesIfNeeded(keyValueFilters['sensitivityLabel'] || [])) {
        needUpdate = true;
      }
    }

    let values: string[] = [];

    values = 'service' in keyValueFilters ? keyValueFilters['service'] : [];
    needUpdate = this.serviceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'actor' in keyValueFilters ? keyValueFilters['actor'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.actorFilters.getOptions$().value, values)) {
      this.actorFilters = new DynamicMultiSelectFilters([], [], FilterTypesEnum.Actor);
      this._actorOnUpdate(values);
    }
    needUpdate = this.actorFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'agent' in keyValueFilters ? keyValueFilters['agent'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.agentFilters.getOptions$().value, values)) {
      this.agentFilters = new DynamicMultiSelectFilters([], [], FilterTypesEnum.AIAgentName);
      this._agentOnUpdate(values);
    }
    needUpdate = this.agentFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      this._updateAllOptions();
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: {
            q: filtersQuery,
          },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    let needUpdate = false;
    if (this.dataTypeFilters.getSelectedOptions().length > 0) {
      if (this.dataTypeFilters.updateIfNeeded([])) {
        needUpdate = true;
      }
    }
    if (this.sensitivityLabelFilters.getSelectedOptions().length > 0) {
      if (this.sensitivityLabelFilters.updateIfNeeded([])) {
        needUpdate = true;
      }
    }
    if (this.serviceFilters.getSelectedOptions().length > 0) {
      if (this.serviceFilters.updateIfNeeded([])) {
        needUpdate = true;
      }
    }
    if (this.actorFilters.getSelectedOptions().length > 0) {
      if (this.actorFilters.updateIfNeeded([])) {
        needUpdate = true;
      }
    }
    if (this.agentFilters.getSelectedOptions().length > 0) {
      if (this.agentFilters.updateIfNeeded([])) {
        needUpdate = true;
      }
    }
    return needUpdate;
  }

  public getDynamicFilters(): string[] {
    return [FilterTypesEnum.Actor, FilterTypesEnum.AIAgentName];
  }

  private _initializeFilters() {
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.search = new TextFilter();
    this.dataTypeFilters = new MultiSelectFilters(DataLayerItemTypeSelectOptions);
    this.sensitivityLabelFilters = new MultiSelectFilters([]);
    this.serviceFilters = new MultiSelectFilters(FindingAiServiceSelectOptions);
    this.actorFilters = new DynamicMultiSelectFilters([], [], FilterTypesEnum.Actor);
    this.agentFilters = new DynamicMultiSelectFilters([], [], FilterTypesEnum.AIAgentName);
    this._updateAllOptions();
  }

  public updateSensitivityLabelOptions(labels: string[]): void {
    const options: ISelectOption[] = labels.map((label) => ({
      id: `${FilterTypesEnum.SensitivityLabel}-${label}`,
      value: label,
      displayName: this._capitalizeLabel(label),
      filterType: FilterTypesEnum.SensitivityLabel,
      categoryName: FilterTypesEnum.SensitivityLabel.toString(),
      withoutBeautifyDisplayName: true,
    }));

    const shouldUpdateSelectedOptions = this.sensitivityLabelFilters.updateOptions(options);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _capitalizeLabel(label: string): string {
    if (!label) {
      return label;
    }
    return label
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private _updateAllOptions() {
    const allOptions: ISelectOption[] = [
      ...this.dataTypeFilters.getOptions$().value,
      ...this.sensitivityLabelFilters.getOptions$().value,
      ...this.serviceFilters.getOptions$().value,
      ...this.actorFilters.getOptions$().value,
      ...this.agentFilters.getOptions$().value,
    ];
    this.allOptions$.next(allOptions);
  }

  private _actorOnUpdate(actorsIds: string[]) {
    this._fetchAndUpdateFilterOptions({
      ids: actorsIds,
      filterName: 'mainActorDisplayName',
      filterType: FilterTypesEnum.Actor,
      filters: this.actorFilters,
    });
  }

  private _agentOnUpdate(agentsIds: string[]) {
    this._fetchAndUpdateFilterOptions({
      ids: agentsIds,
      filterName: 'aiAgentDisplayName',
      filterType: FilterTypesEnum.AIAgentName,
      filters: this.agentFilters,
      transformDisplayName: (text) => this._displayCamelCaseStringPipe.transform(text),
    });
  }

  private _fetchAndUpdateFilterOptions(config: FetchFilterOptionsConfig) {
    const { ids, filterName, filterType, filters, transformDisplayName } = config;

    this._dataLayerHttpService.getDisplayNameById(filterName, ids).subscribe({
      next: (autocompleteResults: AutocompleteSearchResultsV2Model[]) => {
        const options: ISelectOption[] = autocompleteResults?.map((result) => {
          return {
            id: result.id,
            value: result.id,
            displayName: transformDisplayName ? transformDisplayName(result.resultText) : result.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption;
        });

        const shouldUpdateSelectedOptions = filters.updateOptions(Object.values(options));

        this._updateAllOptions();
        if (shouldUpdateSelectedOptions) {
          this.onUpdate$.next(this.getSelectedFilters());
        }
      },
      error: () => {
        this._updateAllOptions();
      },
    });
  }
}
